import React from 'react'
import PropTypes from 'prop-types'
import { Route, Switch, Redirect } from 'react-router-dom'
import { connect } from 'react-redux'
import Layout from './Layout'
import {
  Products,
  SearchHistory,
  SearchOrderDetails,
  AllPlanAgreements,
  PlanAgreementDetails,
  BenefitDistributionsDetails,
} from './views'
import { ServicesAgreementStatus } from 'config/automatic-rollovers'
import { selectors as productSelectors } from './reducer'

import { ProductAgreementType } from '../../config/products'
import NotificationsPlanAgreements from './views/NotificationsPlanAgreements'
import NotificationsAgreementDetails from './views/NotificationsAgreementDetails'
import TaxableAccountsServiceAgreements from './views/TaxableAccountsServiceAgreements'
import TaxableAccountsAgreementDetails from './views/TaxableAccountsAgreementDetails'

const propTypes = {
  match: PropTypes.object.isRequired,
  hasAccessToSearchServices: PropTypes.bool.isRequired,
  hasAccessToAutomaticRollovers: PropTypes.bool.isRequired,
  hasAccessToBenefitDistributions: PropTypes.bool.isRequired,
  hasAccessToNotifications: PropTypes.bool.isRequired,
  hasAccessToTaxableAccounts: PropTypes.bool.isRequired,
  currentUser: PropTypes.object,
}

const defaultProps = {}

function Routes({
  match: { path },
  hasAccessToSearchServices,
  hasAccessToAutomaticRollovers,
  hasAccessToBenefitDistributions,
  hasAccessToNotifications,
  hasAccessToTaxableAccounts,
  currentUser,
}) {
  return (
    <Switch>
      <Route
        exact
        path={path + '/'}
        render={(routeProps) => {
          return (
            <Layout>
              <Products {...routeProps} />
            </Layout>
          )
        }}
      />
      {hasAccessToSearchServices && (
        <Route
          path={path + '/search/history'}
          render={(routeProps) => {
            return (
              <Layout>
                <SearchHistory {...routeProps} />
              </Layout>
            )
          }}
        />
      )}
      {hasAccessToSearchServices && (
        <Route
          path={path + '/search/:orderId'}
          render={(routeProps) => {
            return (
              <Layout>
                <SearchOrderDetails {...routeProps} />
              </Layout>
            )
          }}
        />
      )}
      {hasAccessToBenefitDistributions && (
        <Route
          path={path + '/benefit-distributions'}
          render={(routeProps) => (
            <ConnectedBenefitDistributionsRoutes
              currentUser={currentUser}
              {...routeProps}
            />
          )}
        />
      )}
      {hasAccessToBenefitDistributions && (
        <Redirect
          path={path + '/benefit-distributions/home'}
          to={path + '/benefit-distributions/history'}
        />
      )}
      {hasAccessToAutomaticRollovers && (
        <Route
          path={path + '/automatic-rollovers'}
          component={AutomaticRolloverRoutes}
        />
      )}
      {hasAccessToAutomaticRollovers && (
        <Redirect
          path={path + '/automatic-rollovers/home'}
          to={path + '/automatic-rollovers/services-agreements'}
        />
      )}
      {hasAccessToNotifications && (
        <Route
          path={path + '/notification-services'}
          component={NotificationsRoutes}
        />
      )}
      {hasAccessToNotifications && (
        <Redirect
          path={path + '/notification-services/home'}
          to={path + '/notification-services/history'}
        />
      )}
      {hasAccessToTaxableAccounts && (
        <Route
          path={path + '/taxable-accounts'}
          component={TaxableAccountsRoutes}
        />
      )}
      <Redirect path="*" to="/home" />
      {/* adding a Redirect to handle direct navigation to /products */}
    </Switch>
  )
}

AutomaticRolloverRoutes.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
}

function AutomaticRolloverRoutes({ match: { path } }) {
  return (
    <Switch>
      <Route
        exact
        path={path + '/services-agreements'}
        render={(routeProps) => {
          return (
            <Layout>
              <AllPlanAgreements
                agreementsType={ProductAgreementType.ARO}
                {...routeProps}
              />
            </Layout>
          )
        }}
      />
      <Route
        path={path + '/services-agreements/:servicesAgreementId'}
        render={(routeProps) => {
          return (
            <Layout>
              <PlanAgreementDetails
                agreementsType={ProductAgreementType.ARO}
                {...routeProps}
              />
            </Layout>
          )
        }}
      />
      <Redirect path="*" to={path + '/services-agreements'} />
    </Switch>
  )
}

BenefitDistributionsRoutes.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
  bdPlanAgreements: PropTypes.array,
  currentUser: PropTypes.object,
}

function BenefitDistributionsRoutes({ match: { path }, bdPlanAgreements }) {
  return (
    <Switch>
      <Route
        exact
        path={path + '/history'}
        render={(routeProps) => (
          <Layout>
            <AllPlanAgreements
              agreementsType={ProductAgreementType.BD}
              {...routeProps}
            />
          </Layout>
        )}
      />
      <Route
        path={path + '/history/:servicesAgreementId'}
        render={(routeProps) => {
          const { servicesAgreementId } = routeProps.match.params
          const serviceAgreement = bdPlanAgreements?.find(
            (bdPlanAgreement) =>
              bdPlanAgreement.serviceAgreementId === servicesAgreementId
          )

          const shouldShowPlanAgreementDetails =
            Object.hasOwn(serviceAgreement ?? {}, 'agreementStatus') ||
            serviceAgreement?.addendumStatus ===
              ServicesAgreementStatus.COMPLETED

          return (
            <Layout>
              {shouldShowPlanAgreementDetails ? (
                <PlanAgreementDetails
                  agreementsType={ProductAgreementType.BD}
                  {...routeProps}
                />
              ) : (
                <BenefitDistributionsDetails
                  agreementsType={ProductAgreementType.BD}
                  {...routeProps}
                />
              )}
            </Layout>
          )
        }}
      />
      <Redirect path="*" to={path + '/history'} />
    </Switch>
  )
}

const ConnectedBenefitDistributionsRoutes = connect((state) => ({
  bdPlanAgreements: productSelectors.bdPlanAgreements(state),
}))(BenefitDistributionsRoutes)

NotificationsRoutes.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
}

function NotificationsRoutes({ match: { path } }) {
  return (
    <Switch>
      <Route
        exact
        path={path + '/history'}
        render={(routeProps) => (
          <Layout>
            <NotificationsPlanAgreements
              agreementsType={ProductAgreementType.NOTIFICATIONS}
              {...routeProps}
            />
          </Layout>
        )}
      />
      <Route
        path={path + '/history/:servicesAgreementId'}
        render={(routeProps) => {
          return (
            <Layout>
              <NotificationsAgreementDetails
                agreementsType={ProductAgreementType.ARO}
                {...routeProps}
              />
            </Layout>
          )
        }}
      />
      <Redirect path="*" to={path + '/history'} />
    </Switch>
  )
}

TaxableAccountsRoutes.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string.isRequired,
  }).isRequired,
}

function TaxableAccountsRoutes({ match: { path } }) {
  return (
    <Switch>
      <Route
        exact
        path={path + '/services-agreements'}
        render={(routeProps) => (
          <Layout>
            <TaxableAccountsServiceAgreements {...routeProps} />
          </Layout>
        )}
      />
      <Route
        path={path + '/services-agreements/:servicesAgreementId'}
        render={(routeProps) => {
          return (
            <Layout>
              <TaxableAccountsAgreementDetails {...routeProps} />
            </Layout>
          )
        }}
      />
      <Redirect path="*" to={path + '/services-agreements'} />
    </Switch>
  )
}

Routes.propTypes = propTypes
Routes.defaultProps = defaultProps

export default Routes
